// The skill pack manifest (M4-02; 02 §Reserved "Agents and crews"; 09 §Herk-AI-OS): the
// metadata CompanyOS adds around the Herk skill format, which has none. It sits beside `SKILL.md`
// as `manifest.json` and is part of the pack's content hash, so a pack's declared needs can never
// change without its hash changing. Runs pin `(skill_pack_id, content_hash)`.
//
// `invocation` maps the format's `disable-model-invocation` (and the upstream `openai.yaml`
// `allow_implicit_invocation: false`): `user_only` packs run only when the person asks for them.
// `source_ref` is the provenance of an adapted pack (upstream, commit, SPDX, copyright, trademark
// notes) and is null for a CompanyOS original; the trademark lint does not read it, so the
// upstream's name may appear there and nowhere else.
import { z } from "zod";

/** A pack key: the mount directory name (`/skills/<key>`) and the frontmatter `name`. */
export const SKILL_PACK_KEY_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const SEMVER_RE = /^(0|[1-9]\d{0,5})\.(0|[1-9]\d{0,5})\.(0|[1-9]\d{0,5})$/;
/** Tool, connection and action-kind names: lowercase dotted identifiers (`files.read`). */
const IDENT_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;

/** The owner's context layers a pack may read or write (agent-os.md §12, `crew_context_layer`). */
export const CONTEXT_LAYERS = [
  "manual",
  "identity",
  "business",
  "priorities",
  "voice",
  "connections",
  "decisions",
  "captures",
  "audits",
] as const;
export type ContextLayer = (typeof CONTEXT_LAYERS)[number];

export const SKILL_PACK_INVOCATIONS = ["model", "user_only"] as const;

const ident = z.string().max(64).regex(IDENT_RE);
const identList = z
  .array(ident)
  .max(32)
  .refine((xs) => new Set(xs).size === xs.length, "duplicate entry");
const layerList = z
  .array(z.enum(CONTEXT_LAYERS))
  .refine((xs) => new Set(xs).size === xs.length, "duplicate layer");

export const skillPackSourceRefSchema = z.strictObject({
  upstream_url: z.url({ protocol: /^https$/ }).max(200),
  upstream_commit: z.string().regex(/^[0-9a-f]{40}$/),
  upstream_path: z.string().min(1).max(200),
  license_spdx: z.string().regex(/^[A-Za-z0-9.+-]{1,40}$/),
  copyright: z.string().min(1).max(200),
  trademark_notes: z.string().max(500),
});
export type SkillPackSourceRef = z.infer<typeof skillPackSourceRefSchema>;

/**
 * TEMPLATES-T1 (agent-templates design §1, §6): the connection families a v2 pack's default access
 * names. `mail`, `calendar`, `message`, `crm`, `ledger`, `social` and `intake` reach outside
 * through a connection (`CONNECTED_FAMILIES`); the rest are CompanyOS's own surfaces or the
 * platform's (web search, Recall).
 */
export const SKILL_ACCESS_FAMILIES = [
  "mail",
  "calendar",
  "message",
  "crm",
  "ledger",
  "social",
  "intake",
  "docs",
  "board",
  "chat",
  "kpis",
  "web",
  "recall",
  // HTVA-AUDIT P4: a render service (HTML → PNG/MP4) and an image integration (portraits),
  // reached through a connection, so the packs that need them mount only when they are open.
  "render",
  "image",
] as const;
export type SkillAccessFamily = (typeof SKILL_ACCESS_FAMILIES)[number];
export const CONNECTED_FAMILIES: readonly SkillAccessFamily[] = [
  "mail",
  "calendar",
  "message",
  "crm",
  "ledger",
  "social",
  "intake",
  "render",
  "image",
];

/** R read, A act with approval, D act directly, M manage (design §4 "Access"). */
export const ACCESS_LEVELS = ["R", "A", "D", "M"] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];
export const accessRank = (l: AccessLevel | "never" | null | undefined): number =>
  l === "M" ? 4 : l === "D" ? 3 : l === "A" ? 2 : l === "R" ? 1 : 0;

/** Actions no layer may ever let an agent take directly (integrations §2.6). */
export const NEVER_DIRECT_ACTIONS = ["payment.action", "files_share"] as const;

const limitsSchema = z.strictObject({
  daily_acts: z.int().min(1).max(10000).optional(),
  recipient_domains: z
    .array(z.string().regex(/^[a-z0-9.-]{1,253}$/))
    .max(50)
    .optional(),
});

export const accessDefaultSchema = z
  .strictObject({
    level: z.enum(ACCESS_LEVELS),
    /** One action of the family at its own level (`mail.send`: A); `never` forbids it. */
    actions: z.record(ident, z.enum([...ACCESS_LEVELS, "never"])).optional(),
    /** The level an action reaches once probation ends (`mail.label`: D). */
    after_probation: z.record(ident, z.enum(["A", "D"])).optional(),
    limits: limitsSchema.optional(),
  })
  .superRefine((v, ctx) => {
    for (const a of NEVER_DIRECT_ACTIONS) {
      const lv = v.actions?.[a];
      if (lv === "D" || lv === "M" || v.after_probation?.[a] === "D")
        ctx.addIssue({ code: "custom", message: `${a} is never Direct`, path: ["actions", a] });
    }
  });
export type AccessDefault = z.infer<typeof accessDefaultSchema>;

/** Paths of resources a v2 pack lists (exemplars, tests): markdown under `references/`. */
const resourcePath = z.string().regex(/^references\/[a-z0-9][a-z0-9/_-]{0,120}\.md$/);
const resourceList = z
  .array(resourcePath)
  .min(1)
  .max(16)
  .refine((xs) => new Set(xs).size === xs.length, "duplicate path");

const manifestV1 = z.strictObject({
  format: z.literal("herk-skill"),
  format_version: z.literal(1),
  key: z.string().regex(SKILL_PACK_KEY_RE),
  name: z.string().min(1).max(80),
  version: z.string().regex(SEMVER_RE, "semver MAJOR.MINOR.PATCH"),
  description: z.string().min(1).max(280),
  invocation: z.enum(SKILL_PACK_INVOCATIONS),
  argument_hint: z.string().min(1).max(120).nullable(),
  requires_tools: identList,
  requires_connections: identList,
  action_kinds: identList,
  reads_layers: layerList,
  writes_layers: layerList,
  source_ref: skillPackSourceRefSchema.nullable(),
  license_note: z.string().min(1).max(500),
});

/**
 * TEMPLATES-T1: manifest v2. Everything of v1, plus the pack's default access per connection
 * family, the agent action classes it uses, the KPI keys it can feed, and its exemplars and
 * tests (markdown under `references/`, checked to exist and to have the shape the lint reads).
 */
const manifestV2 = manifestV1.extend({
  format_version: z.literal(2),
  default_access: z.partialRecord(z.enum(SKILL_ACCESS_FAMILIES), accessDefaultSchema),
  policy_classes: z
    .array(z.string().regex(/^[a-z][a-z_]{0,63}$/))
    .max(16)
    .refine((xs) => new Set(xs).size === xs.length, "duplicate class"),
  kpi_keys: z
    .array(
      z
        .string()
        .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/)
        .max(64),
    )
    .max(16)
    .refine((xs) => new Set(xs).size === xs.length, "duplicate key"),
  exemplars: resourceList,
  tests: resourceList,
});

export const skillPackManifestSchema = z.discriminatedUnion("format_version", [
  manifestV1,
  manifestV2,
]);
export type SkillPackManifest = z.infer<typeof skillPackManifestSchema>;
export type SkillPackManifestV2 = z.infer<typeof manifestV2>;
export const isManifestV2 = (m: SkillPackManifest): m is SkillPackManifestV2 =>
  m.format_version === 2;

/** Numeric comparison of two manifest versions (`1.10.0` > `1.9.3`). */
export function compareSkillPackVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}
